'use client';
import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import moment from 'moment';
import { formatCurrency, formatDateMoment, TranslateText } from '@/helpers/utils/siteUtils';
import InvoiceItems from './invoiceItems';
import {
  DATE_FORMAT,
  DEFAULT_SITE_LANG,
  MONTH_YEAR_FORMAT,
  CURRENT_SECTION,
  INVOICE_TABLE_HEADER,
} from '../../../../../../constants/constant';
import { CREDIT_HISTORY_HEADERS, CREDIT_HISTORY_KEYS } from '../../../customer-detail/constants';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../ui/customAccordian/customAccordian';
import { Table, TableColumnConfig } from '../../../ui/customTable/Table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../ui/tabs';
import { PageTexts } from '../../i18n';
import OrderItem from '../OrderItems/orderLineItems';

const PostOrderActivity = ({ Invoices, payRecords, lineItemsWithAdjustments }: any) => {
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);
  const [currentSection, setCurrentSection] = useState(CURRENT_SECTION.PAY_RECORD);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [invoiceData, setInvoiceData] = useState(Invoices?.Invoice);

  const LineItemsTableHeader: TableColumnConfig<any>[] = [
    {
      key: CREDIT_HISTORY_KEYS.DATE,
      header: CREDIT_HISTORY_HEADERS.DATE,
      render: (data) => <div>{formatDateMoment(data, DATE_FORMAT)}</div>,
    },
    {
      key: CREDIT_HISTORY_KEYS.DESCRIPTION,
      header: CREDIT_HISTORY_HEADERS.DESCRIPTION,
      render: (data, rowData) => {
        return (
          <div className="gbmask">
            {rowData?.paymentType === 'CreditCard' && (
              <section className="flex flex-col">
                <div className="font-semibold">{rowData?.PaymentDetail?.cardIssuerName}</div>
                <div>
                  {Texts.cardFormat}
                  {rowData?.PaymentDetail?.lastDigitsOfPan}
                </div>
                <div className="flex items-center">
                  <span className="mr-1 font-semibold">{Texts.expiration}:</span>
                  <div> {moment(rowData?.PaymentDetail?.expirationDate, 'MMYY').format(MONTH_YEAR_FORMAT)}</div>
                </div>
              </section>
            )}
            {rowData?.paymentType === 'Check' && (
              <section className="flex flex-col">
                <p>Check: {rowData?.PaymentDetail?.checkNumber}</p>
                <p>Batch Number: {rowData?.PaymentDetail?.batchId}</p>
              </section>
            )}
            {rowData?.paymentType === 'MasonCredit' && (
              <section className="flex items-center">{rowData?.paymentType}</section>
            )}
            {rowData?.paymentType === 'Cash' && (
              <section className="flex flex-col">
                <p>{rowData?.paymentType}</p>
                <p>Batch Number: {rowData?.PaymentDetail?.batchId}</p>
              </section>
            )}
          </div>
        );
      },
    },
    {
      key: CREDIT_HISTORY_KEYS.AMOUNT,
      header: CREDIT_HISTORY_HEADERS.PURCHASE,
      render: (data) => {
        return <div>{parseFloat(data) >= 0 ? <div>{formatCurrency(data)}</div> : <div></div>}</div>;
      },
    },
    {
      key: CREDIT_HISTORY_KEYS.AMOUNT,
      header: CREDIT_HISTORY_HEADERS.PAYMENT,
      render: (data) => (
        <div className="text-forestGreen">{parseFloat(data) < 0 ? <>{formatCurrency(data)}</> : <></>}</div>
      ),
    },
    {
      key: CREDIT_HISTORY_KEYS.STATUS,
      header: CREDIT_HISTORY_HEADERS.STATUS,
    },
  ];

  const [onState, setOnState] = useState(Array(invoiceData?.length).fill(false));
  return (
    <Accordion type="single" collapsible className="w-full rounded-md border border-foggyGray px-5 py-1 shadow-lg">
      <AccordionItem value="item-1" className="border-none">
        <AccordionTrigger className="text-lg font-bold hover:no-underline">{Texts.postOrderActivity}</AccordionTrigger>
        <AccordionContent>
          {' '}
          <Tabs
            defaultValue={CURRENT_SECTION.PAY_RECORD}
            onValueChange={(value) => setCurrentSection(value)}
            value={currentSection}
          >
            <section className="mb-4 pt-3">
              <div className="flex justify-between">
                <TabsList>
                  <TabsTrigger
                    value={CURRENT_SECTION.PAY_RECORD}
                    className="mr-5 px-0 pb-1 text-lg font-normal text-[#656668]"
                  >
                    {Texts.payRecord}
                  </TabsTrigger>
                  <TabsTrigger value={CURRENT_SECTION.INVOICE} className="px-0 pb-1 text-lg font-normal text-[#656668]">
                    {Texts.invoice}
                  </TabsTrigger>
                </TabsList>
              </div>

              <section className="mt-10">
                <TabsContent value={CURRENT_SECTION.ADJUSTED_ITEMS}>
                  {lineItemsWithAdjustments?.map((item: any) => (
                    <section key={item?.LineItemInfo?.lineSequence}>
                      {' '}
                      <OrderItem Texts={Texts} item={item} />
                    </section>
                  ))}
                </TabsContent>
                <TabsContent value={CURRENT_SECTION.PAY_RECORD}>
                  <Table columns={LineItemsTableHeader} data={payRecords} alignTextTop />
                  {payRecords?.length === 0 && Texts.noRecordsFound}
                </TabsContent>
                <TabsContent value={CURRENT_SECTION.INVOICE}>
                  <table className="min-w-full">
                    <thead className="border-b bg-[#D9D9D9] text-sm">
                      <tr>
                        {Object.keys(INVOICE_TABLE_HEADER)?.map((items) => (
                          <th className="p-3 text-left text-13 font-semibold" key={items}>
                            {INVOICE_TABLE_HEADER?.[items]}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceData?.map((items: any, index: any) => (
                        <>
                          <tr className={`border-b border-silverGray`} key={items?.invoiceNumber}>
                            <td className="gbmask flex items-center p-3 text-14 font-normal">
                              <button
                                onClick={() => {
                                  const newState = [...onState];
                                  newState[index] = !newState[index];
                                  setOnState([...newState]);
                                }}
                              >
                                {onState[index] ? <ChevronDown /> : <ChevronRight />}
                              </button>
                              {items?.invoiceNumber}
                            </td>
                            <td className="p-3 text-14 font-normal">{items?.date}</td>
                            <td className="p-3 text-14 font-normal">
                              <section className="flex items-center">{items?.numberOfItems}</section>
                            </td>
                            <td className="p-3 text-14 font-normal">{items?.price}</td>
                            <td className="p-3 text-14 font-normal">{items?.tax}</td>
                            <td className="p-3 text-14 font-normal">{items?.shipping}</td>
                            <td className="p-3 text-14 font-normal">{items?.totalAmount}</td>
                          </tr>
                          <tr>
                            <td colSpan={Object.keys(INVOICE_TABLE_HEADER)?.length}>
                              {onState[index] && (
                                <>
                                  {items?.lineItems?.map((items: any, index: number) => (
                                    <section key={items?.lineSequence}>
                                      <InvoiceItems Texts={Texts} lineItem={items} index={index} />
                                    </section>
                                  ))}
                                </>
                              )}
                            </td>
                          </tr>
                        </>
                      ))}
                    </tbody>
                  </table>
                  {invoiceData?.length === 0 && Texts.noRecordsFound}
                </TabsContent>
              </section>
            </section>
          </Tabs>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default PostOrderActivity;
